import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Dropdown } from "flowbite-react";
import { removeToken } from "../utils/permissions";
import { useApplication } from "../contexts/ApplicationContext";
import {
  ADMIN_ONLY,
  ADMIN_OR_OPERATOR,
  WITH_CLIENT_ACCESS,
} from "../utils/permissions";
import {
  MdDashboard,
  MdGroup,
  MdBadge,
  MdPayments,
  MdCampaign,
  MdLocalOffer,
  MdLogout,
  MdKeyboardArrowRight,
  MdAdminPanelSettings,
  MdOutlineViewKanban,
  MdOutlineTaskAlt,
  MdOutlineInsights,
  MdOutlineWorkOutline,
  MdOutlineAccountBalanceWallet,
  MdOutlineMarkEmailRead,
  MdOutlineLightbulb,
  MdOutlineCelebration,
  MdOutlineAutorenew,
} from "react-icons/md";
import { HiMenuAlt2 } from "react-icons/hi";

/*
  Navegação do back-office, agrupada por INTENÇÃO e não por entidade.

  O menu antigo era "Dashboard / Clientes / Usuários / Pagamentos / Cupons /
  Comunicados / Acessos": uma lista de telas. Quem atendia começava o dia num
  painel de gráficos e tinha que decidir sozinho o que fazer.

  Agora o primeiro grupo é TRABALHO — o que precisa de ação hoje — e os
  números viraram ANÁLISE, consultados quando a pergunta é "estamos
  melhorando?" em vez de "o que eu faço agora?".
*/
const menuLinks = [
  { name: "Hoje", link: "/", icon: MdOutlineWorkOutline, section: "TRABALHO" },
  {
    name: "Pipeline",
    link: "/pipeline",
    icon: MdOutlineViewKanban,
    section: "TRABALHO",
  },
  {
    name: "Tarefas",
    link: "/tarefas",
    icon: MdOutlineTaskAlt,
    section: "TRABALHO",
  },
  {
    // Cadastros do evento e a cobrança do plano. Cobrar libera o plano, então
    // é de admin ou operador, como o lançamento manual de pagamento.
    name: "Eventos",
    link: "/eventos",
    icon: MdOutlineCelebration,
    section: "TRABALHO",
    roles: ADMIN_OR_OPERATOR,
  },

  {
    name: "Clientes",
    link: "/clientes",
    icon: MdGroup,
    section: "CARTEIRA",
    roles: WITH_CLIENT_ACCESS,
  },
  {
    name: "Minha carteira",
    link: "/carteira",
    icon: MdOutlineAccountBalanceWallet,
    section: "CARTEIRA",
    roles: WITH_CLIENT_ACCESS,
  },

  {
    name: "E-mail",
    link: "/emails",
    icon: MdOutlineMarkEmailRead,
    section: "CARTEIRA",
  },

  {
    name: "Funil",
    link: "/funil",
    icon: MdOutlineInsights,
    section: "ANÁLISE",
    roles: WITH_CLIENT_ACCESS,
  },
  {
    name: "Métricas",
    link: "/metricas",
    icon: MdDashboard,
    section: "ANÁLISE",
    roles: ADMIN_ONLY,
  },

  {
    name: "Usuários",
    link: "/usuarios",
    icon: MdBadge,
    section: "GESTÃO",
    roles: ADMIN_OR_OPERATOR,
  },
  {
    name: "Pagamentos",
    link: "/pagamentos",
    icon: MdPayments,
    section: "GESTÃO",
    roles: ADMIN_OR_OPERATOR,
  },
  {
    name: "Assinaturas",
    link: "/assinaturas",
    icon: MdOutlineAutorenew,
    section: "GESTÃO",
    roles: ADMIN_OR_OPERATOR,
  },
  { name: "Cupons", link: "/cupons", icon: MdLocalOffer, section: "GESTÃO" },
  {
    name: "Sugestões",
    link: "/sugestoes",
    icon: MdOutlineLightbulb,
    section: "GESTÃO",
  },
  {
    name: "Comunicados",
    link: "/comunicados",
    icon: MdCampaign,
    section: "GESTÃO",
    roles: ADMIN_OR_OPERATOR,
  },

  {
    name: "Acessos da equipe",
    link: "/acessos",
    icon: MdAdminPanelSettings,
    section: "ADMINISTRAÇÃO",
    roles: ADMIN_ONLY,
  },
];

// Telas que não têm item de menu próprio (abertas a partir de outra tela) e
// ainda assim precisam de título na barra de cima.
const EXTRA_TITLES = [
  ["/perfil", "Meu perfil"],
  ["/filas/", "Fila de trabalho"],
  ["/emails/nova", "Nova campanha"],
  ["/emails/", "Campanha"],
  ["/clientes/", "Ficha do cliente"],
];

/*
  Item ativo. "/" só acende no caminho exato — como é prefixo de tudo, a
  comparação por prefixo deixaria "Hoje" aceso em toda tela do painel.
*/
const isActiveLink = (link, pathname) =>
  link === "/"
    ? pathname === "/"
    : pathname === link || pathname.startsWith(`${link}/`);

const groupLinks = (links) =>
  links.reduce((acc, item) => {
    if (!acc[item.section]) acc[item.section] = [];
    acc[item.section].push(item);
    return acc;
  }, {});

const NavItem = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.link}
      onClick={onClick}
      className="group flex items-center gap-3 px-4 py-2.5 rounded-lg mx-2 transition-all duration-200 relative"
      style={{
        backgroundColor: isActive ? "rgba(214, 18, 99, 0.15)" : "transparent",
        color: isActive ? "#f472b6" : "rgba(255,255,255,0.6)",
      }}
    >
      {isActive && (
        <span
          className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r-full"
          style={{ backgroundColor: "#d61263" }}
        />
      )}
      <Icon
        size={18}
        style={{
          color: isActive ? "#d61263" : "rgba(255,255,255,0.45)",
          flexShrink: 0,
        }}
      />
      <span className="text-sm font-medium tracking-wide">{item.name}</span>
      {isActive && (
        <MdKeyboardArrowRight
          size={16}
          className="ml-auto"
          style={{ color: "#d61263" }}
        />
      )}
    </Link>
  );
};

const Sidebar = () => {
  const { user, role, isLoadingUser } = useApplication();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const sidebarRef = useRef(null);
  const menuBtnRef = useRef(null);

  // Enquanto o papel carrega, só o que é de todos: o papel provisório é o de
  // gerente de contas, e o operador veria Clientes e Funil piscarem no menu.
  const groupedLinks = groupLinks(
    menuLinks.filter(
      (item) => !item.roles || (!isLoadingUser && item.roles.includes(role))
    )
  );

  /*
    Título da barra superior. A comparação é por PREFIXO e vence o caminho
    mais longo: com igualdade exata, toda rota aninhada (/clientes/42, a ficha
    de um cliente) caía no título padrão. O "/" é tratado à parte porque é
    prefixo de tudo.
  */
  const titlePage =
    menuLinks
      .filter(
        (item) =>
          item.link !== "/" &&
          (location.pathname === item.link ||
            location.pathname.startsWith(`${item.link}/`))
      )
      .sort((a, b) => b.link.length - a.link.length)[0]?.name ||
    EXTRA_TITLES.find(([prefix]) => location.pathname.startsWith(prefix))?.[1] ||
    (location.pathname === "/" ? "Hoje" : "Back Office");

  const handleToggleSidebar = () => setIsOpen((prev) => !prev);
  const closeSidebar = () => setIsOpen(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target) &&
        menuBtnRef.current &&
        !menuBtnRef.current.contains(e.target) &&
        isOpen
      ) {
        closeSidebar();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  const initials = user?.name
    ? user.name
        .split(" ")
        .slice(0, 2)
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "?";

  return (
    <>
      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`fixed top-0 left-0 z-40 w-64 h-screen transition-transform duration-300 flex flex-col sm:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{
          backgroundColor: "#1e1e1e",
          borderRight: "1px solid rgba(255,255,255,0.06)",
        }}
        aria-label="Sidebar"
      >
        {/* Logo area */}
        <div
          className="flex items-center gap-3 px-5 py-5"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div
            className="flex items-center justify-center w-8 h-8 rounded-lg"
            style={{ backgroundColor: "black" }}
          >
            <img
              src="/assets/favicon.png"
              alt="Logo"
              style={{ height: "18px", width: "18px", objectFit: "contain" }}
            />
          </div>
          <div>
            <p className="font-bold text-white text-sm tracking-wide leading-none">
              Relinq
            </p>
            <p
              className="text-xs mt-0.5"
              style={{ color: "rgba(255,255,255,0.35)" }}
            >
              Back Office
            </p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {Object.entries(groupedLinks).map(([section, items]) => (
            <div key={section} className="mb-4">
              <p
                className="px-6 mb-1 text-xs font-semibold tracking-widest uppercase"
                style={{ color: "rgba(255,255,255,0.25)" }}
              >
                {section}
              </p>
              {items.map((item) => (
                <NavItem
                  key={item.link}
                  item={item}
                  isActive={isActiveLink(item.link, location.pathname)}
                  onClick={closeSidebar}
                />
              ))}
            </div>
          ))}
        </nav>

        {/* User section */}
        <div
          className="px-3 py-4"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div
            className="flex items-center gap-3 px-3 py-3 rounded-xl"
            style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
          >
            <div className="relative flex-shrink-0">
              {user?.imageUrl ? (
                <img
                  src={user.imageUrl}
                  alt={user.name}
                  className="w-9 h-9 rounded-full object-cover"
                />
              ) : (
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{
                    background: "linear-gradient(135deg, #d61263, #f35d74)",
                  }}
                >
                  {initials}
                </div>
              )}
              <span
                className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2"
                style={{ backgroundColor: "#22c55e", borderColor: "#1e1e1e" }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold leading-none truncate">
                {user?.name || "Usuário"}
              </p>
              <p
                className="text-xs mt-0.5 truncate"
                style={{ color: "rgba(255,255,255,0.35)" }}
              >
                {user?.email || ""}
              </p>
            </div>
            <button
              onClick={removeToken}
              className="flex-shrink-0 p-1.5 rounded-lg transition-colors duration-150 hover:bg-red-500/20"
              title="Sair"
            >
              <MdLogout size={16} style={{ color: "rgba(255,255,255,0.4)" }} />
            </button>
          </div>
        </div>
      </aside>

      {/* Topbar */}
      <div className="sm:ml-64">
        <div
          className="flex items-center justify-between px-6"
          style={{
            backgroundColor: "#1e1e1e",
            height: "65px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {/* Left: hamburger (mobile) + breadcrumb */}
          <div className="flex items-center gap-4">
            <button
              ref={menuBtnRef}
              type="button"
              onClick={handleToggleSidebar}
              className="sm:hidden p-1.5 rounded-lg transition-colors duration-150 hover:bg-white/10"
            >
              <HiMenuAlt2 size={22} color="rgba(255,255,255,0.8)" />
            </button>
            <div>
              <div
                className="flex items-center gap-1.5"
                style={{ color: "rgba(255,255,255,0.35)" }}
              >
                <span className="text-xs">Relinq</span>
                <MdKeyboardArrowRight size={14} />
                <span className="text-xs">{titlePage}</span>
              </div>
              <h1 className="text-white font-bold text-base leading-tight">
                {titlePage}
              </h1>
            </div>
          </div>

          {/* Right: user dropdown */}
          <div className="flex items-center gap-3">
            <span
              className="hidden md:block text-sm"
              style={{ color: "rgba(255,255,255,0.5)" }}
            >
              Olá,{" "}
              <span className="text-white font-medium">
                {user?.name?.split(" ")[0] || ""}
              </span>
            </span>

            <Dropdown
              label={
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors duration-150 hover:bg-white/10 cursor-pointer">
                  {user?.imageUrl ? (
                    <img
                      src={user.imageUrl}
                      alt={user.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{
                        background: "linear-gradient(135deg, #d61263, #f35d74)",
                      }}
                    >
                      {initials}
                    </div>
                  )}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    className="hidden sm:block"
                  >
                    <path
                      d="M2 4l4 4 4-4"
                      stroke="rgba(255,255,255,0.4)"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              }
              arrowIcon={false}
              inline
            >
              <Dropdown.Header>
                <span className="block text-sm font-semibold">
                  {user?.name}
                </span>
                <span className="block truncate text-xs text-gray-500 mt-0.5">
                  {user?.email}
                </span>
              </Dropdown.Header>
              <Dropdown.Item className="py-0 px-0">
                <Link
                  to="/perfil"
                  className="w-full py-2 text-left px-4 text-sm"
                >
                  Meu perfil
                </Link>
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={removeToken}
                className="text-red-500 text-sm"
              >
                Sair
              </Dropdown.Item>
            </Dropdown>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
